import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  CheckboxButton,
  CheckboxField,
  Dialog,
  Form,
  Heading,
  Modal,
  ModalOverlay,
  RadioButton,
  RadioField,
  RadioGroup,
  Label,
} from 'react-aria-components';
import { FEEDBACK_LIMIT_VALUES, type FeedbackKind } from '@myrewards/contracts';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { TextField } from '../ui/TextField';
import type { DictationPlatform } from './dictation';
import s from './Feedback.module.css';
import type { MicrophoneHelp } from './voice/micAccess';
import { MicHelp } from './voice/MicHelp';
import v from './voice/Voice.module.css';

// APP-104 §3.3: the typed review. Every word of copy here is fixed by the spec.

export const WORDS_REQUIRED = 'Say or type a few words about what happened.';

export const KIND_CHIPS: readonly { kind: FeedbackKind; label: string }[] = [
  { kind: 'problem', label: 'Something isn’t working' },
  { kind: 'idea', label: 'An idea or request' },
  { kind: 'observation', label: 'Something I noticed' },
];

/** The screenshot as the dialog shows it. */
export type ShotView =
  { state: 'taking' } | { state: 'ready'; url: string } | { state: 'none'; reason: string };

export interface FeedbackReviewProps {
  isOpen: boolean;
  words: string;
  onWords: (words: string) => void;
  /** Shown under the words box and read out with it. */
  wordsError: string | null;
  kind: FeedbackKind | null;
  onKind: (kind: FeedbackKind) => void;
  include: boolean;
  onInclude: (include: boolean) => void;
  shot: ShotView;
  /** The dictation tip's platform; null leaves the tip out. */
  dictation: DictationPlatform | null;
  /** What happened to the voice recording, in fixed words (§3.2); null when there's nothing to say. */
  notice?: string | null;
  /** The microphone didn't open: why, the fix, and Try again (or Record). */
  micHelp?: {
    help: MicrophoneHelp;
    retryLabel: 'Try again' | 'Record';
    onRetry: () => void;
  } | null;
  /** Record more by voice, added to the words; null when voice isn't offered now. */
  onRecord?: (() => void) | null;
  sending: boolean;
  /** Why the last Send didn't go through (fixed words). */
  problem: string | null;
  onSend: () => void;
  /** Throw the draft away and close. */
  onDiscard: () => void;
}

export function FeedbackReview({
  isOpen,
  words,
  onWords,
  wordsError,
  kind,
  onKind,
  include,
  onInclude,
  shot,
  dictation,
  notice = null,
  micHelp = null,
  onRecord = null,
  sending,
  problem,
  onSend,
  onDiscard,
}: FeedbackReviewProps): ReactElement {
  const [asking, setAsking] = useState(false);
  const hasWords = words.trim() !== '';
  // Escape and Discard with words in the box ask first; with none, they just close.
  const tryDiscard = (): void => {
    if (hasWords) setAsking(true);
    else onDiscard();
  };
  // Escape: with words it asks "Discard this feedback?"; at the question it means Keep editing.
  // The listener is added once per opening and reads the latest values from here.
  const latest = useRef({ asking, sending, hasWords, onDiscard });
  useEffect(() => {
    latest.current = { asking, sending, hasWords, onDiscard };
  });
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const now = latest.current;
      if (event.key !== 'Escape' || now.sending) return;
      event.preventDefault();
      event.stopPropagation();
      if (now.asking) setAsking(false);
      else if (now.hasWords) setAsking(true);
      else now.onDiscard();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen]);
  const tipId = 'feedback-dictation-tip';
  const noticeId = 'feedback-voice-notice';
  const describedBy = [notice !== null && noticeId, dictation !== null && tipId]
    .filter((id) => id !== false)
    .join(' ');
  return (
    <>
      <ModalOverlay
        isOpen={isOpen}
        isDismissable={false}
        isKeyboardDismissDisabled
        className={s.overlay}
        data-feedback-ui
      >
        <Modal className={s.modal}>
          <Dialog className={s.dialog}>
            <Heading slot="title" className={`disp ${s.title}`}>
              Send feedback
            </Heading>
            {micHelp !== null && (
              <MicHelp
                help={micHelp.help}
                retryLabel={micHelp.retryLabel}
                onRetry={sending ? null : micHelp.onRetry}
              />
            )}
            {notice !== null && (
              <p id={noticeId} className={v.notice}>
                {notice}
              </p>
            )}
            <Form
              className={s.form}
              validationBehavior="aria"
              onSubmit={(event) => {
                event.preventDefault();
                if (!sending) onSend();
              }}
            >
              <div>
                <TextField
                  label="What happened, or what would help?"
                  placeholder="I expected … but …"
                  multiline
                  value={words}
                  onChange={onWords}
                  maxLength={FEEDBACK_LIMIT_VALUES.wordsChars}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- the dialog opens on purpose, for typing
                  autoFocus={words === ''}
                  isReadOnly={sending}
                  isInvalid={wordsError !== null}
                  errorMessage={wordsError ?? WORDS_REQUIRED}
                  {...(describedBy !== '' && { 'aria-describedby': describedBy })}
                />
                {dictation !== null && <DictationTip id={tipId} platform={dictation} />}
                {onRecord !== null && (
                  <div className={v.record}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onPress={onRecord}
                      isDisabled={sending}
                      icon={<Icon name="mic" size={16} />}
                    >
                      Record
                    </Button>
                  </div>
                )}
              </div>

              <RadioGroup
                value={kind ?? ''}
                onChange={(value) => {
                  onKind(value as FeedbackKind);
                }}
                aria-labelledby="feedback-kind"
                isDisabled={sending}
              >
                <Label id="feedback-kind" className={s.legend}>
                  What kind of feedback?
                </Label>
                <div className={s.chips}>
                  {KIND_CHIPS.map((chip) => (
                    <RadioField key={chip.kind} value={chip.kind}>
                      <RadioButton className={s.chip}>
                        {({ isSelected }) => (
                          <>
                            {isSelected && <Icon name="check" size={14} strokeWidth={3} />}
                            {chip.label}
                          </>
                        )}
                      </RadioButton>
                    </RadioField>
                  ))}
                </div>
              </RadioGroup>

              <div className={s.shot}>
                <div className={s.preview}>
                  {shot.state === 'ready' ? (
                    <img
                      src={shot.url}
                      alt="The page you are sending feedback about: the whole page, with the part you can see outlined"
                    />
                  ) : (
                    <p className={s.previewNote} role="status">
                      {shot.state === 'taking'
                        ? 'Taking the screenshot…'
                        : `No screenshot: ${shot.reason}`}
                    </p>
                  )}
                </div>
                {/* D29: the row is there before the checkbox is, so nothing below it moves. */}
                <div className={s.include}>
                  {shot.state === 'ready' && (
                    <CheckboxField isSelected={include} onChange={onInclude} isDisabled={sending}>
                      <CheckboxButton className={s.check}>
                        {({ isSelected }) => (
                          <>
                            <span className={s.box} aria-hidden="true">
                              {isSelected && <Icon name="check" size={14} strokeWidth={3.2} />}
                            </span>
                            Include this screenshot
                          </>
                        )}
                      </CheckboxButton>
                    </CheckboxField>
                  )}
                </div>
              </div>

              <p className="visually-hidden" role="status">
                {shot.state === 'ready' ? 'Screenshot ready' : ''}
              </p>

              <div className={s.alsoIncluded}>
                <h3 className={s.sectionLabel}>Also included</h3>
                <details className={s.included}>
                  <summary>
                    What’s included
                    <Icon name="chevronDown" size={16} className={s.chevron} />
                  </summary>
                  <ul>
                    <li>The page you’re on</li>
                    <li>Your roles (and Preview as, if you’re previewing)</li>
                    <li>Your browser and screen size</li>
                    <li>The app’s version</li>
                    <li>
                      The last 50 requests the app made, without anything you typed in a search
                    </li>
                    <li>Recent errors</li>
                    <li>The last 20 pages you visited</li>
                  </ul>
                </details>
              </div>
              <p className={s.goesTo}>Your feedback goes to the MyRewards System Admin team.</p>

              {problem !== null && (
                <p className={s.problem} role="alert">
                  {problem}
                </p>
              )}
              <div className={s.actions}>
                <Button variant="ghost" onPress={tryDiscard} isDisabled={sending}>
                  Discard
                </Button>
                <Button
                  type={sending ? 'button' : 'submit'}
                  variant="navy"
                  aria-busy={sending}
                  aria-disabled={sending}
                  data-pending={sending || undefined}
                >
                  {sending ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </Form>
          </Dialog>
        </Modal>
      </ModalOverlay>
      <ModalOverlay
        isOpen={isOpen && asking}
        isDismissable={false}
        isKeyboardDismissDisabled
        className={s.overlay}
        data-feedback-ui
      >
        <Modal className={s.modal}>
          <Dialog role="alertdialog" className={s.confirm}>
            <Heading slot="title" className={`disp ${s.title}`}>
              Discard this feedback?
            </Heading>
            <div className={s.actions}>
              <Button
                variant="ghost"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- the question's safe answer
                autoFocus
                onPress={() => {
                  setAsking(false);
                }}
              >
                Keep editing
              </Button>
              <Button
                variant="navy"
                onPress={() => {
                  setAsking(false);
                  onDiscard();
                }}
              >
                Discard
              </Button>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
}

/** Plain text with semantic keys: a pointer to the operating system's own dictation. */
function DictationTip({ id, platform }: { id: string; platform: DictationPlatform }): ReactElement {
  return (
    <p id={id} className={s.tip}>
      {platform === 'windows' ? (
        <>
          Prefer to talk? Press <kbd>Windows</kbd> + <kbd>H</kbd>, then speak.
        </>
      ) : (
        <>
          Prefer to talk? Press <kbd>Fn</kbd> twice, then speak.
        </>
      )}
    </p>
  );
}
